/**
 * Extraction de la silhouette réelle d'un logo via son canal alpha.
 * Un PNG/SVG détouré est nesté sur sa FORME, pas sur son rectangle.
 * Une image totalement opaque (JPG, PNG sans transparence) reste un rectangle.
 */

const SAMPLE_MAX = 256;
const ALPHA_THRESHOLD = 16;

export interface ShapeMask {
	/** true = pixel appartient à la forme */
	data: Uint8Array;
	w: number;
	h: number;
}

/** null = image opaque (ou indéchiffrable) → traiter comme un rectangle plein */
const cache = new Map<string, Promise<ShapeMask | null>>();

function loadImage(src: string): Promise<HTMLImageElement> {
	return new Promise((resolve, reject) => {
		const img = new Image();
		img.onload = () => resolve(img);
		img.onerror = () => reject(new Error('image load failed'));
		img.src = src;
	});
}

async function computeMask(src: string): Promise<ShapeMask | null> {
	try {
		const img = await loadImage(src);
		const iw = img.naturalWidth || img.width;
		const ih = img.naturalHeight || img.height;
		if (!iw || !ih) return null;
		const scale = Math.min(1, SAMPLE_MAX / Math.max(iw, ih));
		const w = Math.max(1, Math.round(iw * scale));
		const h = Math.max(1, Math.round(ih * scale));
		const canvas = document.createElement('canvas');
		canvas.width = w;
		canvas.height = h;
		const ctx = canvas.getContext('2d', { willReadFrequently: true });
		if (!ctx) return null;
		ctx.drawImage(img, 0, 0, w, h);
		const px = ctx.getImageData(0, 0, w, h).data;
		const data = new Uint8Array(w * h);
		let transparent = 0;
		for (let i = 0; i < w * h; i++) {
			const a = px[i * 4 + 3];
			if (a > ALPHA_THRESHOLD) data[i] = 1;
			else transparent++;
		}
		// tout opaque (ou quasi) → aucun intérêt : rectangle
		if (transparent < (w * h) * 0.02) return null;
		return { data, w, h };
	} catch (_e) {
		return null;
	}
}

/** Masque de forme mis en cache par src (les data URLs sont stables par item). */
export function getShapeMask(src: string): Promise<ShapeMask | null> {
	let p = cache.get(src);
	if (!p) {
		p = computeMask(src);
		cache.set(src, p);
	}
	return p;
}
